/** @jsxImportSource minimal-view */

import { Param, PreviewService, createPreviewService, previews } from '../vm/interface'
import { Deferred, checksum, luminate } from 'everyday-utils'
import { element, part, queue, view, web } from 'minimal-view'
import { Sound, listSounds, saveSound } from './api'
import { audio } from './audio'
import { PREVIEW_LENGTH } from './constants'
import { IS_DEV, IS_FIREFOX, IS_MOBILE } from './env'
import { Icon } from './icon'
import { skin } from './skin'
import { SoundEditor } from './sound-editor'
import { SoundView } from './sound-view'
import { ThemePicker } from './theme-picker'
// import { AsciiSvg } from './util/ascii-svg'
import { classes } from './util/classes'
import { Slider } from './slider'
import { hmr } from './hmr'
import { PFFT } from 'pretty-fast-fft'
import { Transport } from './transport'
import { Mixer } from './mixer'
// import { SoundDetail } from './sound-detail'

export type App = typeof App.State

export let app: App

export const App = hmr(web(view('app',
  class props {
    rootDir?= IS_DEV ? '/web' : '/'
  },
  class local {
    host = element
    bodyStyle = document.createElement('style')

    live = !!(localStorage.live ?? true)
    stabilize = !!(localStorage.stabilize ?? true)

    sounds: Sound[] = []
    soundsMap?: Map<string, Sound>
    sound?: Sound
    soundEditor?: SoundEditor
    edited: Map<string, Sound> = new Map(localStorage.edited ? JSON.parse(localStorage.edited) as [string, Sound][] : [])
    editedChk: number = 0

    analyser?: PFFT
    preview?: PreviewService

    userColor: string = '#888'

    showAbout = IS_FIREFOX

    selectedParam: Param | false = false
  },
  function actions({ $, fn, fns }) {
    return fns(new class actions {
      save = fn(({ soundEditor }) => async () => {
        try {
          $.edited.delete($.sound!.id)
          const code = soundEditor.$.code!
          const sound = await saveSound(code)
          $.sounds = [sound, ...$.sounds]
          $.sound = sound
          $.editedChk++
          history.replaceState(null, '', `/${sound.id}`)
          setTimeout(() => {
            sound.el?.scrollIntoView({ inline: 'center', block: 'center', behavior: 'smooth' })
          }, 100)
        } catch (error) {
          alert((error as Error)?.message)
        }
      })

      discard = () => {
        $.edited.delete($.sound!.id)
        $.editedChk++
        history.replaceState(null, '', `/${$.sound!.id}`)
      }

      handleSoundSelect = fn(({ soundEditor }) => (sound: Sound) => () => {
        if ($.sound === sound) {
          audio.$.reset()
        }
        else {
          $.sound = sound

          if ($.edited.has(sound.id)) {
            history.replaceState(null, '', `/${sound.id}?edited---press-+-to-save`)
            soundEditor.$.build = null
            soundEditor.$.code = $.edited.get(sound.id)!.code
          }
          else {
            history.replaceState(null, '', `/${sound.id}`)
            soundEditor.$.build = null
            soundEditor.$.code = $.sound.code
          }
        }
      })

      createPreview = fn(({ preview }) => queue.atomic(async (sound: Sound) => {
        const deferred = Deferred<[Float32Array, Float32Array]>()
        previews.set(sound.id, deferred)
        preview.render(sound.id, sound.code)
        this.debounceChangePreview()
        return deferred.promise
      }))

      debounceChangePreview = queue.debounce(1000)(() => {
        // @ts-ignore
        $.createPreview = this.createPreviewThrottled
      })

      createPreviewThrottled = fn(({ preview }) => queue.throttle(500)(async (sound: Sound) => {
        const deferred = Deferred<[Float32Array, Float32Array]>()
        previews.set(sound.id, deferred)
        preview.render(sound.id, sound.code)
        return deferred.promise
      }))
    })
  },
  function effects({ $, fx, deps, refs }) {
    app = $.host as any
    // console.log(app)
    skin.fx(({ fonts, colors }) => {
      $.bodyStyle.textContent = /*css*/`
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
      }

      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        background: ${colors.bg};
        color: ${colors.fg};
        font-family: ${fonts.sans};
        font-size: 13px;
      }

      .user {
        z-index: 99999;
        width: 10px;
        height: 10px;
        background: ${colors.purple};
        border-radius: 100%;
        position: absolute;
        pointer-events: none;
        border: 2px solid ${colors.bg};
      }

      @font-face {
        font-family: "Medula One";
        font-style: normal;
        font-weight: 400;
        src: url("https://fonts.gstatic.com/s/medulaone/v19/YA9Wr0qb5kjJM6l2V0yuoiYgsg.woff2") format("woff2");
      }
      `

      document.head.appendChild($.bodyStyle)

      $.css = /*css*/`
      * {
        --brightPurple: ${colors.brightPurple};
        --brightGreen: ${colors.brightGreen};
      }
      & {
        box-sizing: border-box;
        display: flex;
        flex-flow: row nowrap;
        align-items: flex-start;
        width: 100%;
        height: 100%;
        margin: 0 auto;
      }

      h1 {
        padding: 0 10px;
        margin: 0;
        background: ${colors.black};
        color: ${colors.white};
        font-family: ${fonts.sans};
        font-size: 25pt;
        font-weight: normal;

        em {
          all: unset;
          color: ${colors.yellow};
        }
      }

      .about {
        position: fixed;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        justify-content: center;

        &-inner {
          width: 500px;
          height: 400px;
          background: ${luminate(colors.bg, 0.05)};
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 20px 0px #000;
        }

        &-logo {
          width: 100%;
          display: flex;
          flex-flow: column nowrap;
          align-items: center;
          justify-content: center;

          /* svg {
            fill: transparent;
            stroke: ${colors.purple};
            stroke-width: 2.9px;
            width: 90px;
            height: 90px;

            path.wave {
              stroke-width: 3px;
              stroke: ${colors.brightCyan};
            }
          } */

          svg {
            fill: transparent;
            stroke: ${colors.purple};
            stroke-width: 2.9px;
            width: 90px;
            height: 90px;

            path.wave {
              stroke-width: 1px;
              stroke: ${colors.brightCyan};
            }
          }

          &-text {
            display: flex;
            /* font-family: "Medula One", serif; */
            font-size: 44px;
            letter-spacing: 2px;
            /* transform-origin: 50% 50%; */
            /* transform: scale(1.57, 1); */
            color: ${colors.green};
          }
        }
      }

      ${SoundEditor} {
        position: absolute;
        top: 0;
        width: calc(100% - 45px - 100px);
        height: 100%;
      }

      .mixer {
        position: absolute;
        top: 0;
        right: 165px;
        width: 150px;
        user-select: none;
        touch-action: none;

        &-item {
          margin: 0 0 4px 0;
          height: 29px;
          width: 150px;
          display: flex;
          flex-flow: column nowrap;

          &-top {
            display: flex;
            flex-flow: row nowrap;
            min-height: 80%;
          }

          .slider {
            padding: 0px 0 0px 0px;
            width: 100%;
            box-sizing: border-box;
          }

          .rms {
            height: 5px;
            width: 100%;
            background: #0f0;
          }

          &-buttons {
            display: flex;
            flex-flow: row nowrap;
            align-items: stretch;
            justify-content: center;

            button {
              all: unset;
              width: 20px;
              margin: 0px 0px 0px;
              text-align: center;
              font-family: ${fonts.mono};
              font-size: 9px;

              &.mute {
                background: ${luminate(colors.red, -.35)};
                &:hover {
                  background: ${luminate(colors.brightRed, -.3)};
                }
                &.active {
                  background: ${luminate(colors.brightRed, -.1)};
                  &:hover {
                    background: ${luminate(colors.brightRed, -.02)};
                  }
                }
                &:active {
                  background: ${luminate(colors.brightRed, -0)} !important;
                }
              }

              &.solo {
                background: ${luminate(colors.cyan, -.35)};
                &:hover {
                  background: ${luminate(colors.cyan, -.25)};
                }
                &.active {
                  background: ${luminate(colors.cyan, -.15)};
                  &:hover {
                    background: ${luminate(colors.cyan, -.12)};
                  }
                }
                &:active {
                  background: ${luminate(colors.cyan, -.0)} !important;
                }
              }
            }
          }
        }
      }

      .sounds {
        position: absolute;
        top: 0;
        right: 45px;
        width: 100px;
        height: 100%;
        overflow-y: scroll;
        user-select: none;
        touch-action: none;
        &::-webkit-scrollbar {
          width: 7px;
        }
        &::-webkit-scrollbar-track {
          /* background: #000; */
        }
        &::-webkit-scrollbar-thumb {
          background: #333;
        }

        &-item {
          all: unset;
          position: relative;
          width: 100%;

          &.active {
            background: ${luminate(colors.bg, 0.05)};
          }
          &.edited::before {
            content: '';
            background: ${colors.yellow};
            position: absolute;
            width: 8px;
            height: 8px;
            left: 0.75px;
            top: 2px;
            z-index: 1;
            border-radius: 100%;
          }
        }
      }

      .transport {
        position: absolute;
        right: 0;
        top: 0;
        background: ${luminate(colors.bg, 0.05)};
        font-family: ${fonts.mono};
        box-sizing: border-box;
        padding: 5px 4px 0;
        width: 45px;
        height: 100%;
        display: flex;
        flex-flow: column wrap;
        justify-content: space-between;

        div {
          width: 100%;
          display: flex;
          flex-flow: column nowrap;
        }

        div:last-child {
          padding-bottom: 10px;
        }

        svg {
          * {
            stroke: currentColor;
            fill: transparent;
          }

          &.logo {
            position: relative;
            left: -0.25px;
            stroke-width: 1.6px;
            fill: transparent;
            margin-bottom: -5px;

            path.wave {
              stroke: ${colors.cyan};
            }
            path.pot {
              display: none;
              stroke: ${colors.purple};
            }
          }
        }

        button {
          --color: ${colors.cyan};
          all: unset;
          cursor: pointer;
          font-family: ${fonts.mono};
          font-size: 12px;
          color: ${colors.brightBlack};
          padding: 2px 5px;

          &.play {
            --color: ${colors.brightGreen};
          }

          &.stop {
            --color: ${colors.brightPurple};
          }

          ${Icon} {
            width: 100%;
          }

          &:hover,
          &.active {
            color: var(--color);
          }
          &.save {
            color: ${colors.brightYellow};
          }
          &.active.discard,
          &:hover.discard {
            color: ${colors.brightRed};
          }
        }
      }
      `
    })

    fx(({ rootDir }) => {
      skin.$.rootDir = rootDir
    })

    fx(() => audio.fx(({ analyser }) => {
      $.analyser = analyser
    }))

    fx(() => skin.fx(({ colors }) => {
      const values = Object.values(colors).filter((x) => typeof x === 'string').slice(1)
      localStorage.userColor = $.userColor = values[Math.random() * values.length | 0]
    }))

    fx.debounce(1000)(({ edited, editedChk }) => {
      localStorage.edited = JSON.stringify([...edited])
    })

    fx(async () => {
      $.sounds = IS_DEV
        ? (await listSounds()).slice(0, 2)
        : (await listSounds()) //.slice(0, 1)

      queueMicrotask(() => {
        fx(({ sounds, soundsMap, edited, soundEditor }) => soundEditor.fx(({ code }) => {
          const id = checksum(code).toString(36)
          // console.log('here', id)
          if (!$.sound) {

            // for (const [key, s] of $.edited) {
            //   if (s.id === id) {
            //     $.sound = soundsMap.get(key)!
            //     return
            //   }
            // }

            const pathId = location.pathname.slice(1)
            if (pathId.length && soundsMap.has(pathId)) {
              const sound = soundsMap.get(pathId)!
              $.sound = sound
              soundEditor.$.code = edited.get(sound.id)?.code ?? sound.code
              setTimeout(() => {
                sound.el?.scrollIntoView({ inline: 'center', block: 'center', behavior: 'smooth' })
              }, 1500)
            }
            else {
              const { selectedSoundId } = localStorage
              if (selectedSoundId?.length) {
                if (soundsMap.has(selectedSoundId)) {
                  const sound = soundsMap.get(selectedSoundId)!
                  $.sound = sound
                  setTimeout(() => {
                    sound.el?.scrollIntoView({ inline: 'center', block: 'center', behavior: 'smooth' })
                  }, 1500)
                  return
                }
              }
              $.sound = { id, code }
            }
          }
          else {
            if (!soundsMap.has(id)) {
              const pathname = `/${$.sound.id}?edited---press-+-to-save`
              const current = location.pathname + location.search
              if (pathname !== current) {
                history.replaceState(null, '', pathname)
              }
              $.edited.set($.sound.id, { id, code })
              $.editedChk++
            }
            else {
              history.replaceState(null, '', `/${$.sound.id}`)
              $.edited.delete($.sound.id)
              $.editedChk++
            }
          }
          // else {
          //   // $.sound.id = id
          //   // $.sound.code = code
          // }
        }))

        fx(({ sounds, sound }) => {
          if (!sounds.some(s => s.id === sound.id)) {
            $.sounds = [sound, ...sounds]
          }
        })
      })

    })

    fx(({ sounds }) => {
      // if (sounds.length) {
      // console.log('yes')
      $.soundsMap = new Map(sounds.map((s) => [s.id, s]))
      // }
    })

    fx(({ sound }) => {
      localStorage.selectedSoundId = sound.id
    })

    // fx(({ edited, sounds, soundsMap, editedChk }) => {
    //   for (const [id, s] of [...edited]) {
    //     if (soundsMap.has(s.id)) {
    //       edited.delete(id)
    //     }
    //   }
    // })

    fx(() => audio.fx(async ({ dspl: _ }) => {
      try {
        $.preview = await createPreviewService(PREVIEW_LENGTH)
      } catch (error) {
        console.warn(error)
        return
      }

      $.preview.onPreview = (id, outs) => {
        previews.get(id)!.resolve(outs)
      }
      $.preview.onError = (id, error) => {
        previews.get(id)!.reject(error)
      }
    }))


    const Main = part((update) => {
      fx(({ live }) => {
        update(
          <>
            <SoundEditor ref={refs.soundEditor} name="main" live={live} />
          </>
        )
      })
    })

    const MixerPart = part((update) => {
      fx(({ soundEditor }) => {
        update(<Mixer soundEditor={soundEditor} />)
      })
    })

    const SoundList = part((update) => {
      // TODO: is 'preview' in the deps necessary?
      fx(({ analyser, sounds, sound, preview: _p, edited, editedChk: _ec }) => {
        update(<div class="sounds">
          {sounds.map((s) => {
            const isEdited = edited.has(s.id)
            return <button key={s}
              title={s.id + `${isEdited ? ' (edited)' : ''}`}
              onref={el => { s.el = el }} class={classes({
                'sounds-item': true,
                edited: isEdited,
                active: sound.id === s.id
              })}
              onclick={IS_MOBILE ? $.handleSoundSelect(s) : () => { }}
              onpointerdown={IS_MOBILE ? () => { } : $.handleSoundSelect(s)}
            >
              <SoundView analyser={analyser} sound={edited.get(s.id) ?? s} />
            </button>
          })}
        </div>)
      })
    })

    // < svg class="logo" width = "32" height = "32" viewBox = "-2 -2 36 36" >
    //   {/* <path class="pot" d="M 4.26 6.27 A 14.72 14.72 90 1 0 27.72 6.27" /> */ }
    //   < path class="wave" d = "M 8.52 14.91 Q 12.26 10.86 16 15.12 T 23.46 15.34" />
    //           </svg >

    const About = part((update) => {
      fx(({ showAbout }) => {
        update(showAbout
          ? <div class="about" onpointerdown={() => {
            $.showAbout = false
            audio.$.createContext()
          }}>
            <div class="about-inner">
              <div class="about-logo">
                <svg class="logo" width="32" height="32" viewBox="7 7 22 22">
                  <path class="wave" d="M 8.52 14.91 Q 12.26 10.86 16 15.12 T 23.46 15.34" />
                </svg>
                <div class="about-logo-text">ravescript</div>
              </div>
            </div>
          </div>
          : <></>
        )
      })
    })

    fx(() => {
      $.view = <>
        <ThemePicker />
        <Main />
        <MixerPart />
        <SoundList />
        <Transport />
        <About />
      </>
    })
  }
)))
